import React, { useCallback } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Flex, Link, Text
} from 'components/ui';
import {
    colors, mediaQueries, radii, shadows, space
} from 'style/config';
import BenefitIconList from 'components/Content/BeautyInsider/BeautyInsiderBenefits/BenefitIconList';
import Empty from 'constants/empty';
import getBenefitsViewState from 'components/Content/BeautyInsider/BeautyInsiderBenefits/benefitsViewState';
import { renderModal } from 'utils/globalModals';
import anaConsts from 'analytics/constants';

const getSection = props => props.section || props.featuredBenefits || getBenefitsViewState(props).featuredBenefits;
const getLinks = section => section?.links || Empty.Array;
const getHref = link => link?.href || link?.url || link?.targetUrl;
const getLabel = link => link?.label || link?.text || link?.title;

/**
 * Renders the tier-specific featured Benefits card and its Contentful-controlled links.
 */
const FeaturedBenefitsSection = props => {
    const section = getSection(props);
    const benefits = section?.benefits || Empty.Array;
    const links = getLinks(section);
    const viewAllBenefitsModal = props.viewAllBenefitsModal;
    const isGrouped = props.isGrouped === true;
    const columns = props.columns || 2;
    const splitAt = columns === 2 && benefits.length > 4 ? Math.ceil(benefits.length / 2) : 0;

    const handleViewAllBenefitsClick = useCallback(
        e => {
            e.preventDefault();

            if (viewAllBenefitsModal?.sid) {
                const modalData = {
                    sid: viewAllBenefitsModal.sid,
                    title: viewAllBenefitsModal.title,
                    width: viewAllBenefitsModal.width ?? 3,
                    isDrawer: viewAllBenefitsModal.isDrawer ?? false,
                    showCloseButton: false,
                    usePageNameFromAnalyticsData: true,
                    analyticsPageName: anaConsts.PAGE_NAMES.BI_BENEFITS_VIEW_ALL
                };

                renderModal(modalData);
            }
        },
        [viewAllBenefitsModal]
    );

    if (!section?.title || !benefits.length) {
        return null;
    }

    const linkList = !!links.length && (
        <Flex
            alignItems='center'
            gap={2}
            flexWrap='wrap'
            marginTop={4}
        >
            {links.map((link, index) => {
                const href = getHref(link);
                const label = getLabel(link);
                const isViewAllBenefits = index === 0 && viewAllBenefitsModal?.sid;

                return (
                    <React.Fragment key={href || label || index}>
                        {index > 0 && (
                            <Text
                                is='span'
                                aria-hidden={true}
                                color='midGray'
                                children='|'
                            />
                        )}
                        <Link
                            href={isViewAllBenefits ? undefined : href}
                            color='blue'
                            children={label}
                            onClick={isViewAllBenefits ? handleViewAllBenefitsClick : undefined}
                        />
                    </React.Fragment>
                );
            })}
        </Flex>
    );

    return (
        <Box
            is='section'
            marginBottom={isGrouped ? 0 : [4, 5]}
            css={isGrouped ? undefined : styles.card}
            data-at={Sephora.debug.dataAt('bi_benefits_featured')}
        >
            <Text
                is='h2'
                fontWeight='bold'
                fontSize={['md', 'lg']}
                lineHeight='tight'
                marginBottom={4}
                children={section.title}
            />
            <BenefitIconList
                benefits={benefits}
                columns={columns}
                splitAt={splitAt}
                splitFooter={splitAt ? linkList : null}
            />
            {!splitAt && linkList}
        </Box>
    );
};

const styles = {
    card: {
        backgroundColor: colors.white,
        borderRadius: radii[2],
        boxShadow: shadows.light,
        padding: space[4],
        [mediaQueries.sm]: {
            padding: space[5]
        }
    }
};

export default wrapFunctionalComponent(FeaturedBenefitsSection, 'FeaturedBenefitsSection');
